import { useEventListener } from '@vueuse/core'

import { SCANNER_INTER_CHAR_DELAY_MS } from '@/composables/use-barcode-scanner/useBarcodeScanner.consts'

/** Set by the pipeline when the Enter being handled ended a scan. Module state, because the pipeline mounts apart. */
let hasPipelineEndedScan = false

/** Called by `useBarcodeScanner` when an Enter ends a valid scan, however long after the code it came. */
const markScanEndedByEnter = () => {
  hasPipelineEndedScan = true
}

/**
 * Tells a scanner's trailing Enter from a person's.
 *
 * ⚠ **The verdict is made on Enter's keydown and read on its keyup.** The search box acts on keyup
 * (`TwnSearchInput`), and by then the scanner pipeline has already taken the scan on keydown and its code is
 * out of the box. Measured at keyup, a slow scanner's Enter looks like a person's, and a form that saves on
 * Enter saves what the merchant typed before the scan. Measured at keydown, it is unmistakable: a scanner
 * sends Enter straight after its last character, and a person never types that fast.
 *
 * So the clock records each character in the capture phase, before any handler, and judges the Enter there.
 * The verdict holds until that Enter's keyup has reached every handler, then clears, so a click afterwards is
 * never mistaken for a scan.
 *
 * VueUse has no such clock: `useMagicKeys` reports which keys are down, not when the last one went down.
 *
 * ⚠ **The clock alone misses a slow scanner.** The pipeline ends a scan on any Enter that arrives before its
 * inactivity timeout, which is longer than this clock's limit. So the pipeline also says when an Enter ended a
 * scan, through `markScanEndedByEnter`, and either one makes the Enter a scanner's.
 */
const useScannerEnterGuard = () => {
  let lastCharacterAt = Number.NEGATIVE_INFINITY
  let isEnterFromScanner = false

  const isCharacter = (event: KeyboardEvent): boolean =>
    event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey

  const forgetEnter = () => {
    isEnterFromScanner = false
    hasPipelineEndedScan = false
  }

  useEventListener(
    document,
    'keydown',
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        // Capture phase, so this runs before the pipeline's own listener marks this Enter.
        hasPipelineEndedScan = false
        isEnterFromScanner = performance.now() - lastCharacterAt < SCANNER_INTER_CHAR_DELAY_MS

        return
      }

      if (isCharacter(event)) lastCharacterAt = performance.now()
    },
    { capture: true }
  )

  // Bubble phase on the document: every handler of the keyup has run by now.
  useEventListener(document, 'keyup', (event: KeyboardEvent) => {
    if (event.key === 'Enter') forgetEnter()
  })

  // An Enter whose keyup never arrived must not outlive it into the next click.
  useEventListener(document, 'pointerdown', forgetEnter, { capture: true })

  /** True only while a scanner's Enter is being handled. */
  const isScannerEnter = (): boolean => isEnterFromScanner || hasPipelineEndedScan

  return { isScannerEnter }
}

export { markScanEndedByEnter, useScannerEnterGuard }
